import { forwardRef, useMemo, type SelectHTMLAttributes } from 'react';
import { listTimeZones } from '@/utils/timezones';
import { SelectField } from './Field';

interface TimeZoneSelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  error?: string;
  hint?: string;
  /**
   * The value currently held by the form. Kept as an option even when the
   * browser lists it under another name (an alias such as `Asia/Calcutta`),
   * so opening and saving a record never changes its zone silently.
   */
  currentValue?: string;
}

/**
 * Every world time zone as a native dropdown, grouped by region and labelled
 * with its current GMT offset. Native on purpose: type-ahead, keyboard and
 * screen-reader support come for free across ~400 options.
 */
export const TimeZoneSelect = forwardRef<HTMLSelectElement, TimeZoneSelectProps>(function TimeZoneSelect(
  { currentValue, ...rest },
  ref,
) {
  const zones = listTimeZones();

  const groups = useMemo(() => {
    const byRegion = new Map<string, typeof zones>();
    for (const zone of zones) {
      byRegion.set(zone.region, [...(byRegion.get(zone.region) ?? []), zone]);
    }
    return [...byRegion.entries()];
  }, [zones]);

  const unknown =
    currentValue !== undefined && currentValue !== '' && !zones.some((zone) => zone.id === currentValue);

  return (
    <SelectField ref={ref} {...rest}>
      {unknown && <option value={currentValue}>{currentValue} (current)</option>}
      {groups.map(([region, members]) => (
        <optgroup key={region} label={region}>
          {members.map((zone) => (
            <option key={zone.id} value={zone.id}>
              {zone.label}
            </option>
          ))}
        </optgroup>
      ))}
    </SelectField>
  );
});
